import React, { useEffect, useRef } from 'react';
import { bool, func, shape, string } from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';

import css from './VideoCard.module.css';

/** A local poster becomes a YouTube player only after an explicit play action. */
const VideoCard = props => {
  const { className, video, active, onPlay, onClose } = props;
  const intl = useIntl();
  const posterRef = useRef(null);
  const playerRef = useRef(null);
  const isBuyer = video.variant === 'buyer';
  const title = intl.formatMessage({ id: video.videoTitleId });

  useEffect(() => {
    if (active) {
      playerRef.current?.focus();
    }
  }, [active]);

  const closeVideo = () => {
    onClose();
    window.requestAnimationFrame(() => posterRef.current?.focus());
  };

  return (
    <article
      className={classNames(css.card, isBuyer ? css.buyer : css.seller, className)}
      aria-label={title}
    >
      <div className={css.videoFrame}>
        {active ? (
          <iframe
            ref={playerRef}
            className={css.player}
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&playsinline=1`}
            title={title}
            width="1920"
            height="1080"
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            ref={posterRef}
            type="button"
            className={css.posterButton}
            onClick={() => onPlay(video.youtubeId)}
            aria-label={intl.formatMessage({ id: 'LandingPage.videos.play' }, { title })}
          >
            <img
              className={css.poster}
              src={video.poster}
              alt=""
              width="1920"
              height="1080"
              loading="lazy"
              decoding="async"
            />
            <span className={css.playButton} aria-hidden="true">
              <svg viewBox="0 0 24 24" className={css.playDrawing}>
                <path d="M8 4v16l12-8Z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {active ? (
        <div className={css.cardFooter}>
          <button
            type="button"
            className={css.closeButton}
            onClick={closeVideo}
            aria-label={intl.formatMessage({ id: 'LandingPage.videos.close' })}
            title={intl.formatMessage({ id: 'LandingPage.videos.close' })}
          >
            <svg viewBox="0 0 24 24" className={css.closeDrawing} aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      ) : null}
    </article>
  );
};

VideoCard.propTypes = {
  className: string,
  video: shape({
    variant: string.isRequired,
    youtubeId: string.isRequired,
    poster: string.isRequired,
    videoTitleId: string.isRequired,
  }).isRequired,
  active: bool.isRequired,
  onPlay: func.isRequired,
  onClose: func.isRequired,
};

export default VideoCard;
